import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Save, Loader2, MapPin, Clock, Plus, X, Camera, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { DashboardLayout } from "./Dashboard";
import DeleteAccountSection from "../../components/DeleteAccountSection";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

// Custom marker icon
const locationIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map click handler component
const LocationPicker = ({ position, setPosition }) => {
  useMapEvents({
    click: (e) => {
      setPosition([e.latlng.lat, e.latlng.lng]);
    }
  });

  return position ? <Marker position={position} icon={locationIcon} /> : null;
};

const PharmacyProfile = () => {
  const { t } = useTranslation("pharmacy");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Form state
  const [profile, setProfile] = useState({
    entity_name: "",
    entity_type: "",
    address: "",
    city: "",
    commune: "",
    country: "RDC",
    phone: "",
    whatsapp: "",
    latitude: null,
    longitude: null,
    services: [],
    photo_url: null
  });

  // Opening hours
  const [openingHours, setOpeningHours] = useState({
    monday: { open: "08:00", close: "18:00", closed: false },
    tuesday: { open: "08:00", close: "18:00", closed: false },
    wednesday: { open: "08:00", close: "18:00", closed: false },
    thursday: { open: "08:00", close: "18:00", closed: false },
    friday: { open: "08:00", close: "18:00", closed: false },
    saturday: { open: "08:00", close: "13:00", closed: false },
    sunday: { open: "", close: "", closed: true }
  });

  // Map position
  const [mapPosition, setMapPosition] = useState(null);

  // Services
  const [newService, setNewService] = useState("");
  const availableServiceKeys = ["delivery", "guard24", "advice", "vaccination", "bloodPressure"];
  const serviceLabel = (key) => t(`profile.serviceOptions.${key}`);
  const availableServices = availableServiceKeys.map(serviceLabel);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get("/pharmacy/profile");
      const data = response.data;

      setProfile({
        entity_name: data.entity_name || "",
        entity_type: data.entity_type || "",
        address: data.address || "",
        city: data.city || "",
        commune: data.commune || "",
        country: data.country || "RDC",
        phone: data.phone || "",
        whatsapp: data.whatsapp || "",
        latitude: data.latitude,
        longitude: data.longitude,
        services: data.services || [],
        photo_url: data.photo_url || null
      });

      if (data.opening_hours && Object.keys(data.opening_hours).length > 0) {
        setOpeningHours((prev) => ({
          ...prev,
          ...data.opening_hours
        }));
      }

      if (data.latitude && data.longitude) {
        setMapPosition([data.latitude, data.longitude]);
      }
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Format opening hours
      const formattedHours = {};
      Object.entries(openingHours).forEach(([day, hours]) => {
        if (!hours.closed) {
          formattedHours[day] = {
            open: hours.open,
            close: hours.close
          };
        }
      });

      const updateData = {
        ...profile,
        latitude: mapPosition ? mapPosition[0] : profile.latitude,
        longitude: mapPosition ? mapPosition[1] : profile.longitude,
        opening_hours: formattedHours
      };

      await apiClient.put("/pharmacy/profile", updateData);

      toast.success(t("profile.saved"));
    } catch (error) {
      toastApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);

      const response = await apiClient.post("/pharmacy/photo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setProfile(prev => ({ ...prev, photo_url: response.data.photo_url }));
      toast.success(t("profile.photoUpdated"));
    } catch (error) {
      toastApiError(error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addService = (service) => {
    if (!profile.services.includes(service)) {
      setProfile((prev) => ({
        ...prev,
        services: [...prev.services, service]
      }));
    }
  };

  const removeService = (service) => {
    setProfile((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s !== service)
    }));
  };

  const handleInputChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleHoursChange = (day, field, value) => {
    setOpeningHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title={t("profile.title")}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("profile.establishmentTitle")}>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Photo Upload Card */}
          <Card className="border-0 shadow-soft" data-testid="photo-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                {t("profile.photoTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                {/* Photo Preview */}
                <div className="relative">
                  {profile.photo_url ? (
                    <img 
                      src={profile.photo_url} 
                      alt={profile.entity_name}
                      className="w-32 h-32 object-cover rounded-2xl border-2 border-slate-200"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-300">
                      <Camera className="h-10 w-10 text-slate-400" />
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-3">
                    {t("profile.photoDesc")}
                  </p>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e.target.files[0])}
                  />
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {profile.photo_url ? t("profile.changePhoto") : t("profile.addPhoto")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info Card */}
          <Card className="border-0 shadow-soft" data-testid="basic-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                {t("profile.basicInfo")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("profile.entityName")}</Label>
                  <Input
                    value={profile.entity_name}
                    onChange={(e) => handleInputChange("entity_name", e.target.value)}
                    className="h-11 rounded-xl"
                    data-testid="entity-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.type")}</Label>
                  <Input
                    value={profile.entity_type}
                    disabled
                    className="h-11 rounded-xl bg-slate-50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("profile.address")}</Label>
                <Input
                  value={profile.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="h-11 rounded-xl"
                  data-testid="address-input"
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t("profile.city")}</Label>
                  <Input
                    value={profile.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    className="h-11 rounded-xl"
                    data-testid="city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.commune")}</Label>
                  <Input
                    value={profile.commune}
                    onChange={(e) => handleInputChange("commune", e.target.value)}
                    className="h-11 rounded-xl"
                    data-testid="commune-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.country")}</Label>
                  <Input
                    value={profile.country}
                    onChange={(e) => handleInputChange("country", e.target.value)}
                    className="h-11 rounded-xl"
                    data-testid="country-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("profile.phone")}</Label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="h-11 rounded-xl"
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.whatsapp")}</Label>
                  <Input
                    value={profile.whatsapp}
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                    placeholder={t("profile.whatsappPlaceholder")}
                    className="h-11 rounded-xl"
                    data-testid="whatsapp-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location Card */}
          <Card className="border-0 shadow-soft" data-testid="location-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-omu-blue-500" />
                {t("profile.locationTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                {t("profile.locationDesc")}
              </p>
              <div className="h-64 rounded-xl overflow-hidden">
                <MapContainer
                  center={mapPosition || [-4.325, 15.322]}
                  zoom={13}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <LocationPicker position={mapPosition} setPosition={setMapPosition} />
                </MapContainer>
              </div>
              {mapPosition && (
                <p className="text-sm text-slate-500 mt-2">
                  {t("profile.coordinates", { lat: mapPosition[0].toFixed(6), lng: mapPosition[1].toFixed(6) })}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Opening Hours Card */}
          <Card className="border-0 shadow-soft" data-testid="hours-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-omu-blue-500" />
                {t("profile.hoursTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(openingHours).map(([day, hours]) => (
                  <div
                    key={day}
                    className="flex items-center gap-4 p-3 rounded-xl bg-slate-50"
                  >
                    <div className="w-24 font-medium">{t(`profile.days.${day}`)}</div>
                    <div className="flex items-center gap-2 flex-1">
                      <Switch
                        checked={!hours.closed}
                        onCheckedChange={(checked) =>
                          handleHoursChange(day, "closed", !checked)
                        }
                        data-testid={`switch-${day}`}
                      />
                      {hours.closed ? (
                        <span className="text-slate-500">{t("profile.closed")}</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={hours.open}
                            onChange={(e) =>
                              handleHoursChange(day, "open", e.target.value)
                            }
                            className="w-28 h-9 rounded-lg"
                            data-testid={`open-${day}`}
                          />
                          <span>-</span>
                          <Input
                            type="time"
                            value={hours.close}
                            onChange={(e) =>
                              handleHoursChange(day, "close", e.target.value)
                            }
                            className="w-28 h-9 rounded-lg"
                            data-testid={`close-${day}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Services Card */}
          <Card className="border-0 shadow-soft" data-testid="services-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">{t("profile.servicesTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.services.map((service) => (
                  <Badge
                    key={service}
                    variant="secondary"
                    className="cursor-pointer hover:bg-red-100"
                    onClick={() => removeService(service)}
                  >
                    {service}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>

              <div className="space-y-2">
                <Label className="text-sm">{t("profile.addServiceLabel")}</Label>
                <div className="flex flex-wrap gap-2">
                  {availableServices
                    .filter((s) => !profile.services.includes(s))
                    .map((service) => (
                      <Badge
                        key={service}
                        variant="outline"
                        className="cursor-pointer hover:bg-omu-blue-50"
                        onClick={() => addService(service)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {service}
                      </Badge>
                    ))}
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Input
                  placeholder={t("profile.otherServicePlaceholder")}
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  className="h-10 rounded-xl"
                  data-testid="new-service-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (newService.trim()) {
                      addService(newService.trim());
                      setNewService("");
                    }
                  }}
                  data-testid="add-service-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button
            className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                {t("actions.save")}
              </>
            )}
          </Button>

          <DeleteAccountSection />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PharmacyProfile;
